import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, ButtonGroup, DialogContentText, TextField, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DataGridPremium, GridColDef, GridRowModel, itIT } from '@mui/x-data-grid-premium';
import { EnhancedAccordion } from 'components/enhanced/accordion/EnhancedAccordion';
import { EnhancedCard } from 'components/enhanced/card/EnhancedCard';
import { useIsDirector } from 'shared/permissions/useIsDirector';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import dayjs, { type Dayjs } from 'dayjs';
import TenantPage from 'layouts/TenantPage';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from 'store/store';
import { Option } from 'shared/types/general';
import toast from 'shared/utils/toast';
import { v4 as uuidv4 } from 'uuid';
import PurchaseRequestLineForm from '../../purchase-requests/components/form/PurchaseRequestLineForm';
import { IPurchaseOrder, IPurchaseOrderRow } from '../types';
import { contractLineService, productService } from 'shared/services';
import { orderService } from '../api/order.service';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import { EnhancedDialog } from 'components/enhanced';
import { SectionTitle } from 'components/ui/typography';
import { OrderLinesColumns } from '../order-lines';

export const PurchaseOrder = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { purchaseOrderId } = useParams();

  let translationPrefix = 'pages.purchase-orders.new';
  if (purchaseOrderId) translationPrefix = 'pages.purchase-orders.view';

  const sap_active = useAppSelector((state) => state.auth.sap_active);
  const [productToAdd, setProductToAdd] = useState<any>(null);
  const [contractToAdd, setContractToAdd] = useState<any>(null);
  const [maxUnits, setMaxUnits] = useState<number | null>(null);
  const [unitPrice, setUnitPrice] = useState<number | null>(null);
  const [maxDiscountedUnits, setMaxDiscountedUnits] = useState<number | null>(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openHeadEditDialog, setOpenHeadEditDialog] = useState(false);
  const [headRef, setHeadRef] = useState<string>('');
  const [headDate, setHeadDate] = useState<Dayjs | null>(null);
  const [lineToRevert, setLineToRevert] = useState<IPurchaseOrderRow | null>(null);

  const isDisabledEdit = useIsDirector() || useIsTechnician();

  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const [contractOptions, setContractOptions] = useState<Option[]>([]);
  const [warehouseOption, setWarehouseOption] = useState<Option | null>(null);

  const [purchaseOrder, setPurchaseOrder] = useState<IPurchaseOrder>({
    id: null,
    status: null,
    from_request: null,
    supplier: null,
    warehouse: null,
    ref: null,
    created_at: null,
    created_by: null,
    lines: []
  });
  const [newLines, setNewLines] = useState<IPurchaseOrderRow[]>([]);
  const [editedLines, setEditedLines] = useState<IPurchaseOrderRow[]>([]);
  const [deletedLines, setDeletedLines] = useState<IPurchaseOrderRow[]>([]);

  const getCurrentPurchaseOrder = () => {
    orderService.get(+purchaseOrderId).then((pr) => {
      if (!pr?.id) navigate('/tenant/orders');
      setPurchaseOrder(pr);
      if (pr.warehouse) {
        warehouseService.get(pr.warehouse).then((w) => {
          setWarehouseOption({ id: w.id, code: w.code });
        });
      } else {
        setWarehouseOption(null);
      }
    });
  };

  useEffect(() => {
    if (purchaseOrderId) {
      getCurrentPurchaseOrder();
    }
  }, []);

  useEffect(() => {
    if (productToAdd) {
      setContractToAdd(null);
      setContractOptions([]);
      contractLineService
        .getAllBaseInformation({ product_id: productToAdd.id, active: true })
        .then(setContractOptions);
    } else {
      setContractToAdd(null);
      setContractOptions([]);
    }
  }, [productToAdd]);

  useEffect(() => {
    if (contractToAdd) {
      contractLineService.getSummary(contractToAdd.id).then((res) => {
        setUnitPrice(+res.unit_price), setMaxUnits(res.available_units_not_blocked);
        setMaxDiscountedUnits(res.available_discounted_units_not_blocked);
      });
    } else {
      setMaxUnits(null);
      setMaxDiscountedUnits(null);
    }
  }, [contractToAdd]);

  useEffect(() => {
    if (warehouseOption) {
      productService.getAllBaseInformation({ warehouse_id: warehouseOption.id }).then((res) =>
        setProductOptions(
          (Array.isArray(res) ? res : []).map((option) => {
            const manufacturerId = option?.manufacturer_id ? ` - ${option.manufacturer_id}` : '';
            return {
              ...option,
              label: `${option?.code ?? ''} - ${(option?.description ?? '').replace(
                `${option?.code ?? ''} - `,
                ''
              )}${manufacturerId}`
            };
          })
        )
      );
    }
  }, [warehouseOption]);

  const saveOrder = (sendEmail: boolean) => {
    orderService
      .updateAndNotify(
        purchaseOrder.id,
        {
          added: newLines,
          edited: editedLines,
          deleted: deletedLines
        },
        sendEmail
      )
      .then(() => {
        getCurrentPurchaseOrder();
        setOpenEditDialog(false);
        setNewLines([]);
        setEditedLines([]);
        setDeletedLines([]);
        toast.success('Ordine salvato.');
      });
  };

  const cancelOrder = (sendEmail: boolean) => {
    orderService.cancelAndNotify(purchaseOrder.id, {}, sendEmail).then((res) => {
      setPurchaseOrder(res);
      setOpenCancelDialog(false);
      toast.success('Ordine annullato.');
    });
    setNewLines([]);
    setEditedLines([]);
    setDeletedLines([]);
  };

  // Utility methods
  const handleDelete = (line: { id: number | string }) => {
    const orderLine = purchaseOrder.lines.find((l) => l.id === line.id);
    setPurchaseOrder((prevState) => {
      const lines = prevState.lines;
      const newLines = lines.map((obj) => {
        if (orderLine.id === obj.id) {
          if (+line.id) {
            setDeletedLines((prevDeleted) => [...prevDeleted, obj]);
          } else {
            setNewLines((prevNewLines) => {
              return prevNewLines.filter((line) => line.id !== orderLine.id);
            });
          }
          return { ...obj, status: 'DELETED' };
        }
        return obj;
      });
      return { ...prevState, lines: newLines };
    });
  };

  const onSubmitRow = (values: { quantity: number; discounted_quantity: number }) => {
    let errors = false;
    if (!contractOptions.map((c) => c.id).includes(values['contract_line'])) {
      toast.error('Seleziona un contratto valido');
      errors = true;
    }
    if (errors) return;
    setPurchaseOrder((prevState) => {
      const added = [];
      if (values.quantity > 0)
        added.push({
          id: uuidv4(),
          ...values,
          status: 'NEW',
          initial_quantity: values.quantity,
          remaining_quantity: values.quantity,
          amount: unitPrice * values.quantity,
          requested_shipment_date:
            values['requested_shipment_date'] &&
            dayjs(values['requested_shipment_date']).format('YYYY-MM-DD')
        });
      if (values.discounted_quantity > 0)
        added.push({
          id: uuidv4(),
          ...values,
          discounted: true,
          initial_quantity: values.discounted_quantity,
          remaining_quantity: values.discounted_quantity,
          status: 'NEW',
          amount: 0,
          requested_shipment_date:
            values['requested_shipment_date'] &&
            dayjs(values['requested_shipment_date']).format('YYYY-MM-DD')
        });
      setNewLines((prevNewLines) => [...prevNewLines, ...added]);
      return {
        ...prevState,
        lines: prevState.lines.concat(added)
      };
    });
  };

  const onRevertLine = (line: IPurchaseOrderRow) => setLineToRevert(line);

  const revertLineStatus = async () => {
    if (!lineToRevert) return;
    try {
      await orderService.revertLinesStatus(purchaseOrder.id, [lineToRevert.id]);
      toast.success(t('pages.purchase-orders.view.revert-status-success'));
      setLineToRevert(null);
      getCurrentPurchaseOrder();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Revert non riuscito.');
      setLineToRevert(null);
    }
  };

  const columns: GridColDef[] = OrderLinesColumns(
    purchaseOrder?.status === 'ACTIVE' && handleDelete,
    onRevertLine,
    purchaseOrder?.sap_id ? true : false
  );

  const processRowUpdate = async (
    newRow: GridRowModel,
    oldRow: GridRowModel
  ): Promise<GridRowModel> => {
    const changedFields = Object.keys(newRow).filter((key) => newRow[key] !== oldRow[key]);

    if (changedFields.length === 0) return oldRow;

    // Costruisci la riga aggiornata - mantieni compatibilità con GridRowModel
    const updatedRow: GridRowModel = { ...newRow };

    changedFields.forEach((field) => {
      const value = newRow[field];
      if (value instanceof Date) {
        updatedRow[field] = dayjs(value).format('YYYY-MM-DD');
      }
    });
    updatedRow['status'] = 'EDITED';

    // Aggiorna purchaseOrder (usa type assertion per la griglia)
    setPurchaseOrder((prevOrder) => ({
      ...prevOrder,
      lines: prevOrder.lines.map((line) =>
        line.id === newRow.id ? ({ ...line, ...updatedRow } as IPurchaseOrderRow) : line
      )
    }));

    // Per editedLines: cast esplicito o crea oggetto tipizzato
    if (typeof newRow.id === 'number' && newRow.id > 0) {
      setEditedLines((prevRows) => {
        const index = prevRows.findIndex((row) => row.id === newRow.id);

        // Cast a IPurchaseOrderRow quando necessario
        const typedRow = updatedRow as IPurchaseOrderRow;

        if (index !== -1) {
          return [...prevRows.slice(0, index), typedRow, ...prevRows.slice(index + 1)];
        }
        return [...prevRows, typedRow];
      });
    } else {
      setNewLines((prevNewLines) =>
        prevNewLines.map((line) =>
          line.id === newRow.id ? ({ ...line, ...updatedRow } as IPurchaseOrderRow) : line
        )
      );
    }

    return updatedRow;
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
  };

  const openHeadEdit = () => {
    setHeadRef(String(purchaseOrder.ref ?? ''));
    setHeadDate(purchaseOrder.created_at ? dayjs(purchaseOrder.created_at) : dayjs());
    setOpenHeadEditDialog(true);
  };

  const handleCloseHeadEditDialog = () => {
    setOpenHeadEditDialog(false);
  };

  const saveHeadEdit = () => {
    if (!headRef?.trim()) {
      toast.error('Il codice ordine è obbligatorio.');
      return;
    }
    if (!headDate) {
      toast.error('Seleziona una data ordine valida.');
      return;
    }
    orderService
      .updateHead(purchaseOrder.id as number, {
        ref: headRef,
        created_at: headDate.format('YYYY-MM-DD')
      })
      .then((res) => {
        setPurchaseOrder(res);
        setOpenHeadEditDialog(false);
        toast.success('Dati ordine aggiornati.');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.error || 'Errore durante il salvataggio.');
      });
  };

  return (
    <>
      {purchaseOrder && (
        <TenantPage
          title={t(`${translationPrefix}.title`, { id: purchaseOrder.ref })}
          subtitle={
            purchaseOrder.status === 'COMPLETED'
              ? t(`${translationPrefix}.subtitle-completed`)
              : t(`${translationPrefix}.subtitle`)
          }
          entityCode={purchaseOrder?.ref + (sap_active ? ` (SAP ID ${purchaseOrder?.sap_id})` : '')}
          menuRight={
            <div className="flex items-center justify-end">
              {purchaseOrder.status === 'COMPLETED' && (
                <div className="pr-4">
                  <CheckCircleOutline color="success" fontSize="large" />
                </div>
              )}
              {purchaseOrder.status === 'ACTIVE' && (
                <IsTenantAdministrator>
                  <ButtonGroup size="small" variant="outlined">
                    <Button
                      disabled={!!purchaseOrder.sap_id || isDisabledEdit}
                      onClick={openHeadEdit}>
                      <EditIcon />
                      Modifica dati ordine
                    </Button>
                    <Button
                      disabled={!newLines.length && !editedLines.length && !deletedLines.length}
                      onClick={() => setOpenEditDialog(true)}>
                      <SaveIcon />
                      Salva modifiche
                    </Button>
                    {purchaseOrder?.lines.every((line) => {
                      if (line?.remaining_quantity === line?.initial_quantity) return true;
                      return false;
                    }) && (
                      <Button onClick={() => setOpenCancelDialog(true)}>
                        <CancelIcon /> Revoca ordine
                      </Button>
                    )}
                  </ButtonGroup>
                </IsTenantAdministrator>
              )}
            </div>
          }>
          {purchaseOrder.id &&
            purchaseOrder.status !== 'COMPLETED' &&
            !isDisabledEdit &&
            !sap_active && (
              <EnhancedAccordion title={t('pages.purchase-requests.new.add-element')}>
                <PurchaseRequestLineForm
                  onSubmit={onSubmitRow}
                  products={productOptions}
                  setProduct={setProductToAdd}
                  contracts={contractOptions}
                  setContract={setContractToAdd}
                  warehouses={warehouseOption ? [warehouseOption] : []}
                  defaultWarehouse={warehouseOption}
                  disableWarehouse={true}
                  maxUnits={maxUnits}
                  maxDiscountedUnits={maxDiscountedUnits}
                />
              </EnhancedAccordion>
            )}
          {purchaseOrder.confirmations && purchaseOrder.confirmations.length > 0 && (
            <div className="my-4">
              <SectionTitle>{t(`${translationPrefix}.supplier_confirmations`)}</SectionTitle>
              <ul className="px-2">
                {purchaseOrder.confirmations.map((confirmation) => (
                  <li
                    key={confirmation.id}
                    className="mb-2 flex flex-wrap items-center gap-2 border-b border-gray-200 pb-2">
                    <div className="flex-1 min-w-[200px]">
                      <Typography variant="body2">
                        {dayjs(confirmation.received_at).format('DD/MM/YYYY HH:mm')} —{' '}
                        {confirmation.sender_email}
                      </Typography>
                      {confirmation.subject && (
                        <Typography variant="caption" color="text.secondary">
                          {confirmation.subject}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block">
                        {t(`${translationPrefix}.supplier_confirmations_lines`, {
                          count: confirmation.lines_confirmed
                        })}
                      </Typography>
                    </div>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CloudDownloadIcon />}
                      onClick={() =>
                        orderService.downloadOrderConfirmation(purchaseOrder.id, confirmation.id)
                      }>
                      {t('global.download')}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Item list */}
          <EnhancedCard
            title={'Dettaglio Voci Ordine'}
            subtitle={"Visualizza, modifica e gestisci le voci inserite nell'ordine di acquisto."}>
            <DataGridPremium
              density="compact"
              initialState={{
                pinnedColumns: {
                  left: ['code', 'approved'],
                  right: ['actions']
                }
              }}
              rows={purchaseOrder.lines.filter((row) => row.status !== 'DELETED')}
              localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
              sx={{ border: 'none' }}
              disableRowSelectionOnClick
              pagination
              columns={columns}
              processRowUpdate={processRowUpdate}
              pageSizeOptions={[5, 10, 20, 50, 100]}
            />
          </EnhancedCard>
          {/* Confirmation dialog */}

          <EnhancedDialog
            open={openEditDialog}
            onClose={handleCloseEditDialog}
            fullWidth
            maxWidth="md"
            title={'Conferma modifica ordine'}
            costumFooter={
              <div className="w-full flex justify-between">
                <Button onClick={handleCloseEditDialog}>{t('actions.cancel')}</Button>
                <div>
                  <Button className="me-2" onClick={() => saveOrder(false)}>
                    {t('actions.confirm')}
                  </Button>
                  <Button variant="contained" onClick={() => saveOrder(true)} autoFocus>
                    {t('actions.confirm-send-email')}
                  </Button>
                </div>
              </div>
            }>
            <DialogContentText id="alert-dialog-description">
              <p>{t('pages.purchase-orders.view.edit-confirmation')}</p>
            </DialogContentText>
          </EnhancedDialog>

          <EnhancedDialog
            open={openCancelDialog}
            onClose={handleCloseCancelDialog}
            maxWidth="md"
            title={'Conferma cancellazione ordine'}
            costumFooter={
              <div className="w-full flex justify-between">
                <Button onClick={handleCloseCancelDialog}>{t('actions.cancel')}</Button>
                <div>
                  <Button className="me-2" onClick={() => cancelOrder(false)}>
                    {t('actions.confirm')}
                  </Button>
                  <Button variant="contained" onClick={() => cancelOrder(true)} autoFocus>
                    {t('actions.confirm-send-email')}
                  </Button>
                </div>
              </div>
            }>
            <DialogContentText id="alert-dialog-description">
              <p>{t('pages.purchase-orders.view.cancel-confirmation')}</p>
            </DialogContentText>
          </EnhancedDialog>

          <EnhancedDialog
            open={openHeadEditDialog}
            onClose={handleCloseHeadEditDialog}
            fullWidth
            maxWidth="sm"
            title={'Modifica codice e data ordine'}
            costumFooter={
              <div className="w-full flex justify-between">
                <Button onClick={handleCloseHeadEditDialog}>{t('actions.cancel')}</Button>
                <Button variant="contained" onClick={saveHeadEdit}>
                  {t('actions.confirm')}
                </Button>
              </div>
            }>
            <DialogContentText id="head-edit-description">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box component="form" noValidate autoComplete="off">
                  <TextField
                    label="Codice ordine"
                    value={headRef}
                    onChange={(e) => setHeadRef(e.target.value)}
                    inputProps={{ maxLength: 100 }}
                    fullWidth
                    required
                  />
                  <Box sx={{ mt: 3 }}>
                    <DatePicker
                      label="Data ordine"
                      value={headDate}
                      onChange={(v) => setHeadDate(v as Dayjs | null)}
                      format="DD/MM/YYYY"
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Box>
                </Box>
              </LocalizationProvider>
            </DialogContentText>
          </EnhancedDialog>
          <EnhancedDialog
            open={!!lineToRevert}
            onClose={() => setLineToRevert(null)}
            maxWidth="sm"
            title={t('pages.purchase-orders.view.revert-status-confirm-title')}
            costumFooter={
              <div className="w-full flex justify-between">
                <Button onClick={() => setLineToRevert(null)}>{t('actions.cancel')}</Button>
                <Button variant="contained" onClick={revertLineStatus} autoFocus>
                  {t('actions.confirm')}
                </Button>
              </div>
            }>
            <DialogContentText id="revert-status-description">
              <p>
                {t('pages.purchase-orders.view.revert-status-confirm-message', {
                  index: (lineToRevert as IPurchaseOrderRow & { index?: number })?.index
                })}
              </p>
            </DialogContentText>
          </EnhancedDialog>
        </TenantPage>
      )}
    </>
  );
};

export default PurchaseOrder;
